
import React, { useState } from 'react';
import { User, Language, IntegrationFlow, AgentProfile, IntegrationApp, ServiceDefinition, AccessToken, SubscriptionRecord, UserStatistics } from '../../types';
import ProfileHeader from './ProfileHeader';
import ProfileBanner from './ProfileBanner';
import ProfileTabs from './ProfileTabs';
import ProfileContent from './ProfileContent';
import ProfileSubscriptionModal from './ProfileSubscriptionModal';
import ProfileKeyModal from './ProfileKeyModal';

interface ProfileComponentProps {
  user: User;
  language: Language;
  activeTab: 'overview' | 'services' | 'flows' | 'agents' | 'integrations' | 'keys' | 'subscriptions';
  currentPlan: any;
  showSubSettings: boolean;
  cancelStep: number;
  servicesData: ServiceDefinition[];
  flows: IntegrationFlow[];
  agents: AgentProfile[];
  integrationApps: IntegrationApp[];
  activeServices: Set<string>;
  totalIntegrationRequests: number;
  totalIntegrationTimeSaved: number;
  // Tokens
  tokens: AccessToken[];
  onCreateToken: (name: string, type: AccessToken['type'], durationDays: number) => void;
  onRevokeToken: (id: string) => void;
  // Subscriptions
  subscriptions: SubscriptionRecord[];
  onSubscriptionAction: (id: string, action: 'cancel' | 'renew' | 'toggle_renew' | 'pause' | 'resume') => void;
  
  onBack: () => void;
  onSetActiveTab: (tab: any) => void;
  onManageBilling: () => void;
  onCloseSubSettings: () => void;
  onSetCancelStep: (step: number) => void;
  onCancelSubscription: () => void;
  onToggleService: (id: string) => void;
  onToggleFlow: (id: string) => void;
  onToggleAgent: (id: string) => void;
  // New
  statistics?: UserStatistics;
}

const ProfileComponent: React.FC<ProfileComponentProps> = (props) => {
  const { 
    user, language, activeTab, currentPlan, showSubSettings, cancelStep, 
    servicesData, flows, agents, integrationApps, activeServices,
    totalIntegrationRequests, totalIntegrationTimeSaved, tokens,
    onCreateToken, onRevokeToken, subscriptions, onSubscriptionAction,
    onBack, onSetActiveTab, onManageBilling, onCloseSubSettings, 
    onSetCancelStep, onCancelSubscription, onToggleService, onToggleFlow, onToggleAgent,
    statistics
  } = props;

  const [showKeyModal, setShowKeyModal] = useState(false);

  return (
    <div className="flex-1 h-full bg-slate-50 dark:bg-[#08080a] overflow-y-auto custom-scrollbar p-4 lg:p-8 pb-24 lg:pb-12 relative">
      <ProfileHeader 
        user={user} 
        language={language} 
        onBack={onBack} 
      />

      <ProfileBanner 
        user={user} 
        language={language} 
        currentPlan={currentPlan} 
        onManageBilling={onManageBilling} 
      />

      <ProfileTabs 
        language={language} 
        activeTab={activeTab} 
        onSetActiveTab={onSetActiveTab} 
      />

      <ProfileContent 
        activeTab={activeTab}
        servicesData={servicesData}
        flows={flows}
        agents={agents}
        integrationApps={integrationApps}
        activeServices={activeServices}
        totalIntegrationRequests={totalIntegrationRequests}
        totalIntegrationTimeSaved={totalIntegrationTimeSaved}
        tokens={tokens}
        subscriptions={subscriptions}
        onToggleService={onToggleService}
        onToggleFlow={onToggleFlow}
        onToggleAgent={onToggleAgent}
        onOpenCreateKey={() => setShowKeyModal(true)}
        onRevokeToken={onRevokeToken}
        onSubscriptionAction={onSubscriptionAction}
        statistics={statistics}
      />

      {showSubSettings && (
        <ProfileSubscriptionModal 
          user={user}
          language={language}
          currentPlan={currentPlan}
          cancelStep={cancelStep}
          onClose={onCloseSubSettings}
          onSetCancelStep={onSetCancelStep}
          onCancelSubscription={onCancelSubscription}
        />
      )}

      {showKeyModal && (
        <ProfileKeyModal 
          language={language}
          onClose={() => setShowKeyModal(false)}
          onCreate={onCreateToken}
        />
      )}
    </div>
  );
};

export default ProfileComponent;
